import { Box, Text } from 'grommet';
import { CSSProperties } from 'react';

import { Spinner } from '../../Spinner';
import { toScoreString } from '../utils';

import { Scorecard, ScorecardHole, ScorecardHoleStatusEnum } from '@drewkimberly/pga-pool-api';

/** A hole slot that may be played (ScorecardHole) or empty (placeholder). */
type HoleSlot = ScorecardHole | { hole_number: number; par: null; score: null; status: null };

/**
 * Pad a partial set of holes to a full 9-hole half.
 * Missing holes get placeholder slots — par is filled from coursePars when available.
 */
function padToNine(
  holes: ScorecardHole[],
  startHole: number,
  coursePars: Map<number, number>
): HoleSlot[] {
  const byNumber = new Map(holes.map((h) => [h.hole_number, h]));
  return Array.from({ length: 9 }, (_, i) => {
    const holeNum = startHole + i;
    return (
      byNumber.get(holeNum) ?? {
        hole_number: holeNum,
        par: coursePars.get(holeNum) ?? null,
        score: null,
        status: null,
      }
    );
  });
}

/**
 * Returns CSS for idiomatic golf scorecard symbols:
 * - Birdie: single circle
 * - Eagle (or better): double circle
 * - Bogey: single square
 * - Double bogey (or worse): double square
 *
 * All symbols and numbers use default text color (currentColor).
 */
function getScoreSymbolStyle(status: ScorecardHoleStatusEnum | null): CSSProperties | undefined {
  const circleColor = 'currentColor';
  const bogeyColor = 'var(--color-bogey)';

  switch (status) {
    case ScorecardHoleStatusEnum.Birdie:
      return {
        border: `1.5px solid ${circleColor}`,
        borderRadius: '50%',
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    case ScorecardHoleStatusEnum.Eagle:
      return {
        border: `1.5px solid ${circleColor}`,
        borderRadius: '50%',
        outline: `1.5px solid ${circleColor}`,
        outlineOffset: 2,
        outlineStyle: 'solid',
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    case ScorecardHoleStatusEnum.Bogey:
      return {
        border: `1.5px solid ${bogeyColor}`,
        borderRadius: 2,
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    case ScorecardHoleStatusEnum.DoubleBogey:
      return {
        border: `1.5px solid ${bogeyColor}`,
        borderRadius: 2,
        outline: `1.5px solid ${bogeyColor}`,
        outlineOffset: 2,
        outlineStyle: 'solid',
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    default:
      return undefined;
  }
}

const cellStyle: CSSProperties = {
  textAlign: 'center',
  padding: '4px 2px',
  verticalAlign: 'middle',
};

const headerCellStyle: CSSProperties = {
  ...cellStyle,
  backgroundColor: 'var(--color-scorecard-header)',
};

const LABEL_WIDTH = 50;

const labelStyle: CSSProperties = {
  ...cellStyle,
  width: LABEL_WIDTH,
  minWidth: LABEL_WIDTH,
  fontWeight: 700,
  textAlign: 'left',
  paddingLeft: 8,
};

function NineHoleGrid({
  holes,
  label,
  totalPar,
  totalStrokes,
}: {
  holes: HoleSlot[];
  label: string;
  totalPar: number;
  totalStrokes: number;
}) {
  const hasAnyScores = holes.some((h) => h.score !== null);

  return (
    <Box style={{ overflowX: 'auto' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr>
            <th style={labelStyle}>
              <Text size="xsmall" weight="bold">
                HOLE
              </Text>
            </th>
            {holes.map((h) => (
              <th key={h.hole_number} style={headerCellStyle}>
                <Text size="xsmall" color="text-weak">
                  {h.hole_number}
                </Text>
              </th>
            ))}
            <th style={headerCellStyle}>
              <Text size="xsmall" weight="bold">
                {label}
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Par row */}
          <tr>
            <td style={labelStyle}>
              <Text size="xsmall" color="text-weak">
                PAR
              </Text>
            </td>
            {holes.map((h) => (
              <td key={h.hole_number} style={cellStyle}>
                <Text size="xsmall" color="text-weak">
                  {h.par ?? '-'}
                </Text>
              </td>
            ))}
            <td style={cellStyle}>
              <Text size="xsmall" color="text-weak" weight="bold">
                {totalPar || '-'}
              </Text>
            </td>
          </tr>
          {/* Score row */}
          <tr>
            <td style={labelStyle}>
              <Text size="xsmall" weight="bold">
                SCORE
              </Text>
            </td>
            {holes.map((h) => {
              if (h.score === null) {
                return (
                  <td key={h.hole_number} style={{ ...cellStyle, padding: '5px 1px' }}>
                    <Text size="xsmall" color="text-weak">
                      -
                    </Text>
                  </td>
                );
              }
              const symbolStyle = getScoreSymbolStyle(h.status);
              return (
                <td key={h.hole_number} style={{ ...cellStyle, padding: '5px 1px' }}>
                  {symbolStyle ? (
                    <span style={symbolStyle}>
                      <Text size="xsmall" weight="bold">
                        {h.score}
                      </Text>
                    </span>
                  ) : (
                    <Text size="xsmall" weight="bold">
                      {h.score}
                    </Text>
                  )}
                </td>
              );
            })}
            <td style={cellStyle}>
              <Text size="xsmall" weight="bold" style={{ fontFamily: 'var(--font-display)' }}>
                {hasAnyScores ? totalStrokes : '-'}
              </Text>
            </td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
}

export interface PlayerPanelScorecardProps {
  scorecard: Scorecard | null;
  isLoading: boolean;
  error: string | null;
  coursePars: Map<number, number>;
}

export function PlayerPanelScorecard({
  scorecard,
  isLoading,
  error,
  coursePars,
}: PlayerPanelScorecardProps) {
  if (isLoading) {
    return (
      <Box align="center" pad="small">
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box align="center" pad="small">
        <Text size="small" color="text-weak">
          {error}
        </Text>
      </Box>
    );
  }

  if (!scorecard || scorecard.holes.length === 0) {
    return (
      <Box align="center" pad="small">
        <Text size="small" color="text-weak">
          No scorecard data available
        </Text>
      </Box>
    );
  }

  const sorted = [...scorecard.holes].sort((a, b) => a.hole_number - b.hole_number);
  const front9 = sorted.filter((h) => h.hole_number <= 9);
  const back9 = sorted.filter((h) => h.hole_number > 9);

  // Use coursePars for full 9-hole par totals (not just played holes)
  const front9Padded = padToNine(front9, 1, coursePars);
  const back9Padded = padToNine(back9, 10, coursePars);

  const sumPar = (holes: HoleSlot[]) => holes.reduce((sum, h) => sum + (h.par ?? 0), 0);
  const front9Par = sumPar(front9Padded);
  const back9Par = sumPar(back9Padded);
  const front9Strokes = front9.reduce((sum, h) => sum + h.score, 0);
  const back9Strokes = back9.reduce((sum, h) => sum + h.score, 0);

  // Par total is known if coursePars covers every hole in the half
  const front9ParKnown = front9Padded.every((h) => h.par !== null);
  const back9ParKnown = back9Padded.every((h) => h.par !== null);

  return (
    <Box gap="small">
      <NineHoleGrid
        holes={front9Padded}
        label="OUT"
        totalPar={front9ParKnown ? front9Par : 0}
        totalStrokes={front9Strokes}
      />
      <NineHoleGrid
        holes={back9Padded}
        label="IN"
        totalPar={back9ParKnown ? back9Par : 0}
        totalStrokes={back9Strokes}
      />
      {front9.length > 0 && back9.length > 0 && (
        <Box direction="row" justify="end" pad={{ horizontal: 'small' }}>
          <Text size="small" weight="bold" style={{ fontFamily: 'var(--font-display)' }}>
            Total: {scorecard.strokes} ({toScoreString(scorecard.to_par)})
          </Text>
        </Box>
      )}
    </Box>
  );
}
